import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../shared/Header';
import AdminSidebar from './AdminSidebar';
import { menuItems, bottomMenuItems, branding } from './AdminMenuConfig.js';
import { SidebarProvider } from '../../../context/SidebarContext';
import { useAuth } from '../../../context/AuthContext';
import useBranch from '../../../hooks/useBranch';

/**
 * AdminLayout Component
 *
 * ENTRY REDIRECT:
 *   When the user lands on /admin or /admin/dashboard, we walk menuItems
 *   and bottomMenuItems in order and redirect to the first page they actually
 *   have permission for — so they never hit an "Access Denied" screen on entry.
 *
 *   • Has permission for dashboard → stay, nothing happens
 *   • No dashboard permission, has bills → redirect to /admin/bills-summary
 *   • No permissions at all → redirect to /select-role
 */
export default function AdminLayout({ children }) {
  const { restaurantName } = useBranch();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only act when landing on the bare Back Office entry paths
    const isEntryPath =
      location.pathname === '/admin' ||
      location.pathname === '/admin/dashboard';

    if (!isEntryPath) return;

    // Walk all items in order, find first path the user can access
    const allItems = [...menuItems, ...bottomMenuItems];
    let firstRoute = null;

    for (const item of allItems) {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      if (hasChildren) {
        const firstChild = item.children.find(
          (child) => !child.permission || hasPermission(child.permission)
        );
        if (firstChild) { firstRoute = firstChild.path; break; }
      } else {
        if (!item.permission || hasPermission(item.permission)) {
          firstRoute = item.path;
          break;
        }
      }
    }

    if (!firstRoute) {
      // No Back Office page is accessible — send back to module selection
      navigate('/select-role', { replace: true });
    } else if (firstRoute !== '/admin/dashboard') {
      // User can't see dashboard but can see something else — go there
      navigate(firstRoute, { replace: true });
    }
    // If firstRoute === '/admin/dashboard', user has access — do nothing, stay put
  }, [location.pathname, hasPermission]);

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
        {/* Shared Header Component */}
        <Header
          restaurantName={restaurantName}
        />

        {/* Main Content Wrapper */}
        <div className="flex flex-1 overflow-hidden">
          {/* Admin Sidebar */}
          <AdminSidebar
            menuItems={menuItems}
            bottomMenuItems={bottomMenuItems}
            branding={branding}
            defaultOpen={true}
          />

          {/* Main Content Area */}
          <main className="flex-1 overflow-auto bg-gray-50">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}